import React from 'react';
import { createRoot } from 'react-dom/client';
import algoliasearch from 'algoliasearch/lite';
import {
    useInstantSearch,
    InstantSearch,
    Configure,
    ClearRefinements,
    InfiniteHits,
    SearchBox,
    Stats,
  } from 'react-instantsearch';

import { CardResourceEntry } from "./resource-card";
import { FilterPanel } from "./filter-panel";

const ALGOLIA_APPLICATION_ID = import.meta.env.VITE_ALGOLIA_APPLICATION_ID;
const ALGOLIA_SEARCH_API_KEY = import.meta.env.VITE_ALGOLIA_SEARCH_API_KEY;
const searchClient = algoliasearch(ALGOLIA_APPLICATION_ID, ALGOLIA_SEARCH_API_KEY);


export default () => {
    const root = createRoot(document.getElementById('app'));
    root.render(<Index/>);
}

// Display the No Results message when no hits have been returned.
function NoResultsBoundary({ children, fallback }) {
    const { results } = useInstantSearch();
    if (!results.__isArtificial && results.nbHits === 0) {
        return (
            <>
            {fallback}
            <div hidden>{children}</div>
            </>
        );
    }
    return children;
}
  
function NoResults() {
    const { indexUiState } = useInstantSearch();
    return (
      <div className="text-center my-12 mx-auto text-lg text-brand-blue-900">
        <p>No results for <q>{indexUiState.query}</q>.</p>
      </div>
    );
}

function MarketFilterPanel(props) {

    let show = true;
    const expand = props.expand;
    const { indexUiState } = useInstantSearch();

    if (indexUiState.refinementList !== undefined) {
        let marketsSelected = "markets" in indexUiState.refinementList;
        let typesSelected = "type" in indexUiState.refinementList;
        if (typesSelected && !marketsSelected) {
            if (!expand && indexUiState.refinementList.type.length === 1 && indexUiState.refinementList.type[0] == 'Environmental Images') {
                show = false;
            }
        }
    }
    if (!show) {
        return;
    }

    return (
        <FilterPanel
            heading='Market'
            attribute='markets'
            expand={expand}
            sortBy={['name:asc']}
            operator="and"
        />
    )
}

function Index() {
// export default () => {
    const searchParams = new URLSearchParams(document.location.search);
    let filterType        = searchParams.get("type") ?? '';
    let filterMarket      = searchParams.get("markets") ?? '';
    let filterDepartment  = searchParams.get("department") ?? '';
    let filterStyle       = searchParams.get("style") ?? '';
    let filterTemperature = searchParams.get("temperature") ?? '';
    let filterCollection  = searchParams.get("collection") ?? '';
    let filterFunction    = searchParams.get("productFunction") ?? '';
    let searchQuery       = searchParams.get("show") ?? '';

    const uiFilterState = Object.assign({},
        filterType === '' ? null : {type: [filterType]},
        filterMarket === '' ? null : {markets: [filterMarket]},
        filterDepartment === '' ? null : {departments: [filterDepartment]},
        filterStyle === '' ? null : {styles: [filterStyle]},
        filterTemperature === '' ? null : {temperatures: [filterTemperature]},
        filterCollection === '' ? null : {collections: [filterCollection]},
        filterFunction === '' ? null : {function: [filterFunction]},
    );


    return (
        <InstantSearch
            searchClient={searchClient}
            indexName="product_resources"
            insights={true}
            initialUiState={{
                product_resources: {
                    query: searchQuery,
                    refinementList: uiFilterState,
                    page: 1
                },
            }}
        >
            <Configure hitsPerPage={12} />
            <div className="bg-brand-neutral-200 relative">
                <div className="accent-banner"></div>
                <div className="accent-banner accent-banner-br"></div>
                <div className="container mx-auto py-8 lg:py-12 xl:py-12 2xl:py-16 relative z-5">
                    <h1 className="text-center font-medium leading-tight mb-4 lg:mb-6 xl:mb-8 text-2xl md:text-3xl lg:text-4xl xl:text-5xl text-brand-blue-900">Resource Library</h1>
                    <SearchBox
                        placeholder="Search resources"
                        classNames={{
                            root: 'w-full max-w-2xl mx-auto',
                            form: 'relative',
                            input: 'w-full border border-brand-neutral-400 outline-brand-blue-100 rounded-md p-4 pl-12',
                            submit: 'absolute left-4 top-4',
                            reset: 'absolute right-4 top-4'
                        }}
                        submitIconComponent={() => (
                            <svg className="w-6 h-auto fill-current text-slate-400" xmlns="http://www.w3.org/2000/svg" width="36" height="36" fill="#000000" viewBox="0 0 256 256"><path d="M229.66,218.34l-50.07-50.06a88.11,88.11,0,1,0-11.31,11.31l50.06,50.07a8,8,0,0,0,11.32-11.32ZM40,112a72,72,0,1,1,72,72A72.08,72.08,0,0,1,40,112Z"></path></svg>
                        )}
                        resetIconComponent={({ classNames }) => (
                            <svg className="w-6 h-auto fill-current text-slate-600" xmlns="http://www.w3.org/2000/svg" width="36" height="36" fill="#000000" viewBox="0 0 256 256"><path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"></path></svg>
                        )}                                    
                    />
                </div>
            </div>
            <div className="container mx-auto">
                <div className="lg:flex gap-12 py-12 lg:py-16 xl:py-24 2xl:py-28">
                    <div className="basis-1/4 lg:sticky self-start top-0">
                            <div className="flex items-center justify-between mt-6 py-6 border-b border-brand-50">
                                <h3 className="font-medium text-xl">Filters</h3>
                                <div className="flex items-center gap-2">
                                        <svg className="text-brand-neutral-600 w-5 h-auto" xmlns="http://www.w3.org/2000/svg" width="36" height="36" fill="#000000" viewBox="0 0 256 256"><path d="M224,128a96,96,0,0,1-94.71,96H128A95.38,95.38,0,0,1,62.1,197.8a8,8,0,0,1,11-11.63A80,80,0,1,0,71.43,71.39a3.07,3.07,0,0,1-.26.25L44.59,96H72a8,8,0,0,1,0,16H24a8,8,0,0,1-8-8V56a8,8,0,0,1,16,0V85.8L60.25,60A96,96,0,0,1,224,128Z"></path></svg>
                                        <ClearRefinements
                                            classNames={{
                                                root: 'flex items-center',
                                                button: 'text-brand-blue-300',
                                                disabledButton: 'text-brand-neutral-600',
                                            }}
                                            translations={{
                                                resetButtonText: 'Reset Filters',
                                            }}
                                        />
                                </div>
                            </div>


                            {/* <ToggleRefinement
                                attribute="is_discontinued"
                                label="Include Discontinued Products"
                                off={false}
                                classNames={{
                                    root: 'py-4 border-b border-brand-50',
                                    label: 'flex items-center',
                                    labelText: 'font-light',
                                    checkbox: 'mr-4 border w-5 h-5 border-brand-blue-300 accent-brand-blue-300 focus:ring-0 rounded-none',
                                }}
                            /> */}

                            <FilterPanel
                                heading='Asset Type'
                                attribute='type'
                                expand={filterType !== ""}
                                sortBy={['name:asc']}
                                operator="or"
                            />
                            
                            <MarketFilterPanel expand={filterMarket !== ""}/>
                            
                            <FilterPanel
                                heading='Department'
                                attribute='departments'
                                expand={filterDepartment !== ""}
                                sortBy={['name:asc']}
                                operator="and"
                            />

                            <FilterPanel
                                heading='Product Style'
                                attribute='styles'
                                expand={filterStyle !== ""}
                                sortBy={['name:asc']}
                                operator="and"
                            />

                            <FilterPanel
                                heading='Temperature'
                                attribute='temperatures'
                                expand={filterTemperature !== ""}
                                sortBy={['name:asc']}
                                operator="and"
                            />

                            <FilterPanel
                                heading='Collection'
                                attribute='collections'
                                expand={filterCollection !== ""}
                                sortBy={['name:asc']}
                                operator="and"
                            />

                            <FilterPanel
                                heading='Function'
                                attribute='function'
                                expand={filterFunction !== ""}
                                sortBy={['name:asc']}
                                operator="and"
                            />

                            <div className="mt-6 mb-12 xl:mt-12 bg-brand-blue-50 p-6 xl:p-8 rounded-lg">
                                <svg className="w-6 h-auto" xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28" fill="none"><path d="M19.8337 11.6667V9.33333C19.8337 6.11167 17.222 3.5 14.0003 3.5C10.7787 3.5 8.16699 6.11167 8.16699 9.33333V11.6667M14.0003 16.9167V19.25M10.267 24.5H17.7337C19.6938 24.5 20.6739 24.5 21.4226 24.1185C22.0812 23.783 22.6166 23.2475 22.9522 22.589C23.3337 21.8403 23.3337 20.8602 23.3337 18.9V17.2667C23.3337 15.3065 23.3337 14.3264 22.9522 13.5777C22.6166 12.9191 22.0812 12.3837 21.4226 12.0481C20.6739 11.6667 19.6938 11.6667 17.7337 11.6667H10.267C8.30681 11.6667 7.32672 11.6667 6.57803 12.0481C5.91946 12.3837 5.38403 12.9191 5.04847 13.5777C4.66699 14.3264 4.66699 15.3065 4.66699 17.2667V18.9C4.66699 20.8602 4.66699 21.8403 5.04847 22.589C5.38403 23.2475 5.91946 23.783 6.57803 24.1185C7.32672 24.5 8.30681 24.5 10.267 24.5Z" stroke="#2678AB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                <div className="text-brand-blue-900 font-medium text-lg my-4">SCC Sales Rep Portal</div>
                                <div className="text-brand-neutral-700 text-sm my-4">Access Sales Reports, Tools, Resources, and the Latest News</div>
                                <a className="button text inline" href="https://reps.structuralconcepts.com" target="_blank">Visit Portal</a>
                            </div>

                    </div>
                    <div className="basis-3/4 relative mt-6 lg:mt-0">
                        <Stats classNames={{root: 'hidden lg:flex ml-auto text-sm text-brand-neutral-600 absolute -translate-y-8 right-0'}}/>
                        <NoResultsBoundary fallback={<NoResults />}>
                            <InfiniteHits
                                hitComponent={CardResourceEntry}
                                showPrevious={false}
                                classNames={{
                                    list: 'grid grid-cols-2 xl:grid-cols-3 gap-4 gap-y-8 md:gap-6 lg:gap-y-10 xl:gap-10',
                                    loadMore: 'text-brand-blue-300 hover:text-brand-blue-500 border border-brand-blue-300 hover:text-brand-blue-300 rounded-md px-8 py-4 mt-8 mx-auto flex justify-center',
                                }}
                                translations={{
                                    showMoreButtonText: 'Load More',
                                }}
                            />
                        </NoResultsBoundary>
                    </div>
                </div>
            </div>
        </InstantSearch>
    );


}